import type {
  Graph,
  EditableGraph,
  ConversationNode,
  EditableNodeType,
  EndConversationNode,
  Edge,
  AnswerId,
  Id,
  UseCase,
  ConditionValue,
  EditableNode,
} from 'src/types/chatbot';
import type {Validation} from 'src/utils/validation';
import type {ConversationBuilderState, ConversationDispatch} from './reducer';

import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import useI18n from 'src/hooks/useI18n';
import invariant from 'invariant';
import kebabCase from 'lodash/kebabCase';
import last from 'lodash/last';
import domToImage from 'dom-to-image';
import fileSaver from 'file-saver';
import {useHistory} from 'src/rerouter';

import {ENABLE_PRESCREENING} from './constants';
import {
  editableNodeTypes,
  validateEditableGraph,
  checkGraph,
  getChildEdges,
  getFirstMessages,
  getRoot,
  createImmutableGraph,
  isBranchableNodeType,
  createEndConversationNode,
} from './utils';
import {
  useContextMenu2,
  SuperMenu,
} from 'src/components/lib/context-menu/context-menu-2';

import classify from 'src/utils/classify';
import {getValidation} from 'src/utils/validation-2';
import clamp from 'lodash/clamp';
import {showGenericError} from 'src/action-creators/modal';
import {captureSentryException} from 'src/utils/sentry';
import useAgencyConfig, {useSchedulerEnabled} from 'src/hooks/useAgencyConfig';
import {
  useReleaseFlag,
  useLiveAgentTransfer,
  useApplicationChatbot,
  useChatbotLiveFlowEditEnabled,
} from 'src/hooks/product-flags';
import {useFlows} from './api-hooks';

import {getAllNamespacedGlobalVariables} from 'src/action-creators/namespaced-global-variables';
import {
  getExternalChatbotVariables,
  getDiscoverJobAttributes,
} from 'src/action-creators/chatbot';

import {SmsContext} from './sms-context';

import ScrollContainer from './scroll-container';
import BranchView from './branch-view';
import ConversationNodeView, {FeedbackNode} from './conversation-node';
import {NodeEditor} from './node-editor/editor';
import Button, {UnstyledButton} from 'src/components/lib/new-button';
import Controls from './controls';
import ArcedPath from './wiggle-path';
import {Shroud} from './shroud';
import ConditionSelector from './condition-selector';
import {getWhatsappDetails} from 'src/selectors/chatbot';
import {HoverError} from './common';
import FeedbackEditor from './node-editor/feedback';
import GenericSidepanel from 'src/components/modals/generic-sidepanel';
import NodeEditorV2 from 'src/components/conversation-builder/node-editor-v2/node-editor-v2';

import ComposeIcon from 'src/images/icons/compose-icon.svg';
import {Icon as GenesisIcon} from '@spaced-out/ui-design-system/lib/components/Icon';

import css from './conversation-builder.css';
import commonCss from './common.css';


type Point = {
  x: number;
  y: number;
};

type Position = {
  left: number;
  top: number;
};

type RenderBranch = Position & {
  edge: Edge;
  height: number;
  outPoint: Point;
};

export type RenderNode = Position &
  Point & {
    bottom: number;
    right: number;
    node: ConversationNode | null | undefined;
    parent: RenderNode | null | undefined;
    parentEdge: Edge | null | undefined;
    children: RenderNode[];
    childEdges: Edge[];
    index: number;
    size: number;
    depth: number;
    inPoint: Point;
    outPoint: Point;
    startPoint: Point;
    turnPoint: number;
    branches?: Array<RenderBranch>;
    jump?: boolean;
  };

export default function ConversationBuilder({
  className,
  style,
  dispatchRef,
  graph: defaultGraph,
  onChange,
  disabled,
  showErrors,
  imageExportState,
  setFlow,
  state,
  dispatch,
  isJourneyPreview,
}: {
  className?: string;
  style?: {
    [key: string]: unknown;
  };
  // NOTE (kyle): this is a hack to give dispatch access to parents.
  // If we find more uses for it in the future, we should absolutely lift
  // the reducer state to the parent scope.
  dispatchRef?: {
    current: ConversationDispatch | null | undefined;
  };
  graph: Graph;
  // NOTE (kyle): onChange will never fire when the editor is disabled
  onChange?: (arg1: Graph) => unknown;
  disabled?: unknown;
  showErrors?: unknown;
  imageExportState: [
    boolean,
    (arg1: boolean | ((arg1: boolean) => boolean)) => void,
  ];
  setFlow?: (arg1: Graph) => void;
  state: ConversationBuilderState;
  dispatch: ConversationDispatch;
  isJourneyPreview: boolean;
}) {
  const router = useHistory();
  const reduxDispatch = useDispatch();
  React.useEffect(() => {
    reduxDispatch(getAllNamespacedGlobalVariables());
    reduxDispatch(getExternalChatbotVariables());
    reduxDispatch(getDiscoverJobAttributes());
  }, []);
  const [showFeedbackEditor, setShowFeedbackEditor] = React.useState(false);
  const isChatbotLiveFlowEnabled = useChatbotLiveFlowEditEnabled();
  const whatsappDetails = useSelector(getWhatsappDetails);
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const nodeTypes = {
    message: t('Message', 'Message'),
    question: t('Question', 'Question'),
    summary: t('Summary', 'Summary'),
    notification: t('Notification', 'Notification'),
    condition: t('Condition', 'Condition'),
    disposition: t('Disposition', 'Disposition'),
    scheduler: t('Meeting', 'Meeting'),
    job_match: t('Job_match', 'Job match'),
    file_upload: t('File_upload', 'File upload'),
    live_agent_transfer: t('Live_Chat', 'Live Chat'),
    link_to_flow: t('Link_To_Flow', 'Link To Flow'),
    end_conversation: t('End_Conversation', 'End Conversation'),
    dynamic_question: t('Dynamic_Question', 'Dynamic Question'),
    text_to_apply: t('Text_to_Apply', 'Text to Apply'),
  } as const;

  const isSms = defaultGraph.medium === 'sms';

  const graph = state.history[state.historyIndex];

  React.useEffect(() => {
    dispatch({
      type: 'RESET_STATE',
      payload: defaultGraph,
    });
  }, [defaultGraph.id]);
  // this is needed for preview because graph comes from state.history,
  // which isn't reset when a different flow is selected because the
  // component doesn't get remounted

  const isMissionBot = graph.bot_type === 'task_oriented';
  const [{flows, loaded}] = useFlows();

  const readOnly = graph.status !== 'init' || Boolean(disabled);

  const couldSaveLiveFlow = readOnly
    ? graph.status === 'success' && isChatbotLiveFlowEnabled
    : !readOnly;

  // @ts-expect-error - TS7030 - Not all code paths return a value.
  React.useEffect(() => {
    if (dispatchRef) {
      dispatchRef.current = dispatch;
      return () => {
        dispatchRef.current = null;
      };
    }
  }, [dispatchRef, dispatch]);

  const firstMessages = new Set(getFirstMessages(graph));

  const entityType = graph.anchor_entity_type;

  // TODO (kyle): worried about perf here
  const [isMounted, setIsMounted] = React.useState(false);

  // TODO (kyle): don't fire `onChange` onmount
  React.useEffect(() => {
    if (isMounted && onChange && couldSaveLiveFlow) {
      onChange(createImmutableGraph(graph));
    } else {
      setIsMounted(true);
    }
    // NOTE (kyle): isMounted does not show up in deps because its change
    // should not trigger this effect.
  }, [graph, disabled]);

  // NOTE (kyle): on first render, center the canvas.
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const scrollRef = React.useRef();
  React.useLayoutEffect(() => {
    const {current} = scrollRef;
    if (current) {
      // @ts-expect-error - TS2339 - Property 'scrollLeft' does not exist on type '{}'. | TS2339 - Property 'clientWidth' does not exist on type '{}'.
      current.scrollLeft = (graphWidth - current.clientWidth) / 2;
    }
  }, []);

  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const backgroundRef = React.useRef();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const containerRef = React.useRef();
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const shroudRef = React.useRef();

  // NOTE (kyle): we have to prerender before we export as a PNG. this allows us
  // to optimize the DOM.
  const [isExportingImage, setIsExportingImage] = imageExportState;
  const handleImageExport = () => {
    if (!isExportingImage) {
      setIsExportingImage(true);
    }
  };

  const slugify = (str: string) =>
    kebabCase(str.trim().replace(new RegExp('[&/:]', 'g'), ''));

  React.useEffect(() => {
    if (isExportingImage) {
      if (containerRef.current) {
        // NOTE (kyle): if `toBlob` fails, we allow the error to bubble uncaught
        domToImage
          // @ts-expect-error - TS2345 - Argument of type '{}' is not assignable to parameter of type 'Node'.
          .toBlob(containerRef.current)
          .then(async (blob) => {
            if (!blob) {
              throw new Error('Failed to export image.');
            }
            fileSaver.saveAs(blob, `${slugify(graph.name)}-${graph.id}.png`);
          })
          .catch((error) => {
            reduxDispatch(
              showGenericError({
                title: t('Export_Failed', 'Export Failed'),
                text: `${t(
                  'Your_export_unexpectedly_failed_If_this_happens_repeatedly_please_notify_us',
                  'Your export unexpectedly failed. If this happens repeatedly, please notify us.',
                )}.`,
              }),
            );
            captureSentryException(error, {
              level: 'error',
            });
          })
          .then(() => {
            setIsExportingImage(false);
          });
      } else {
        setIsExportingImage(false);
      }
    }
  }, [isExportingImage]);

  const rootEdge = getRoot(graph);
  invariant(rootEdge, 'Cannot render without a root edge.');

  const [heightCache, setHeightCache] = React.useState<
    Map<Record<any, any>, number>
  >(new Map());
  const pendingHeights = new Set();
  const handleElementRender = (item: Record<any, any>) => {
    if (!heightCache.has(item)) {
      pendingHeights.add(item);
    }
  };
  const handleSize = (item: Record<any, any>, size: number) => {
    heightCache.set(item, size);
    pendingHeights.delete(item);
    if (pendingHeights.size === 0) {
      setHeightCache(new Map(heightCache));
    }
  };

  const checkCycle = !useReleaseFlag('chatbotIgnoreCycles');
  const validateOpts = {checkCycle} as const;

  const error = showErrors ? validateEditableGraph(graph, validateOpts) : null;
  const warnings = checkGraph(graph);

  const renderTree = createRenderTree(graph, rootEdge);

  // TODO (kyle): tagHeight is the height of the "start" tag but should be
  // programmatic.
  const content = finalizePaths(
    createRenderContent(graph, renderTree, heightCache, tagHeight),
  );
  const graphHeight =
    Math.max(...content.map((renderNode) => renderNode.top)) +
    nodeHeight +
    nodePad +
    bottomPad;
  const minLeft = Math.min(...content.map((renderNode) => renderNode.left));
  const graphWidth =
    Math.max(...content.map((renderNode) => renderNode.right)) -
    minLeft +
    nodePad * 2;

  // @ts-expect-error - TS2322 - Type 'RenderNode | undefined' is not assignable to type 'Position & Point & { bottom: number; right: number; node: ConversationNode | null | undefined; parent: RenderNode | null | undefined; ... 11 more ...; jump?: boolean | undefined; } & { ...; }'.
  const editingNode: RenderNode & {
    node: EditableNode;
  } = content.find(
    (renderNode) => renderNode.node && renderNode.node.id === state.editingNode,
  );
  const insertingAboveRenderNode =
    !editingNode &&
    state.insertingNode &&
    content.find(
      (renderNode) => renderNode.parentEdge?.id === state.insertingNode?.edgeId,
    );

  const editingEdge =
    state.editingEdge != null && graph.edges.get(state.editingEdge);
  const jumpingFromEdge =
    state.jumpingFrom && graph.edges.get(state.jumpingFrom.edgeId);

  const showingShroud =
    (insertingAboveRenderNode &&
      state.insertingNode?.question_type !== 'preset') ||
    editingEdge ||
    editingNode;

  const handleCancelNodeEditorV2 = () => {
    if (editingNode) {
      dispatch({type: 'CANCEL_EDIT_NODE'});
    } else if (state.insertingNode) {
      dispatch({type: 'CANCEL_INSERT'});
    }
  };

  const nodeType = state.insertingNode?.nodeType || editingNode?.node.type;
  let extraSidepanelClassName;
  switch (nodeType) {
    case 'live_agent_transfer':
      extraSidepanelClassName = css.latSidePanel;
      break;
  }

  return (
    <SmsContext.Provider value={isSms}>
      {/* @ts-expect-error - TS2322 - Type '{ [key: string]: unknown; } | undefined' is not assignable to type 'CSSProperties | undefined'. */}
      <div className={classify(css.root, className)} style={style}>
        <ScrollAndControls
          // @ts-expect-error - TS2322 - Type '{ children: (Element | null | undefined)[]; className: string; state: ConversationBuilderState; dispatch: ConversationDispatch; ... 5 more ...; onImageExport: () => void; }' is not assignable to type 'IntrinsicAttributes & { children: any; state: any; dispatch: any; contentHeight: any; contentWidth: any; validateDrag: any; isScrollable: any; disabled: any; onImageExport: any; }'.
          className={css.scrollContainer}
          state={state}
          dispatch={dispatch}
          contentWidth={graphWidth}
          contentHeight={graphHeight}
          // @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type.
          validateDrag={(event) =>
            [
              containerRef.current,
              backgroundRef.current,
              shroudRef.current,
            ].includes(event.target)
          }
          isScrollable={!showingShroud}
          disabled={disabled}
          onImageExport={handleImageExport}
        >
          <div
            className={css.background}
            // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
            ref={containerRef}
            style={{
              height: graphHeight + nodePad * 2 + tagHeight * 2,
              width: graphWidth,
            }}
          >
            <div
              className={classify(
                css.backgroundImage,
                isExportingImage && css.print,
              )}
              // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
              ref={backgroundRef}
            />
            <div className={css.graph} style={{left: '50%'}}>
              <div className={css.tag} style={{left: -tagWidth / 2}}>
                {t('Start', 'Start')}
              </div>
              {content.map((renderNode) => {
                const {inPoint, startPoint, turnPoint, parentEdge} = renderNode;

                const nodeError =
                  renderNode.node && error?.nodes?.[String(renderNode.node.id)];
                const nodeWarning =
                  renderNode.node &&
                  getValidation(warnings, ['nodes', renderNode.node.id]);
                let edgeError;
                let pathContent;
                if (parentEdge) {
                  edgeError = error?.edges?.[String(parentEdge.id)];

                  const isWhatsAppRootQuestion =
                    whatsappDetails?.enable_whatsapp &&
                    renderNode.node?.is_root &&
                    renderNode?.node?.type === 'question';
                  const shouldShowAddButton =
                    !disabled && !isWhatsAppRootQuestion;
                  pathContent = parentEdge.editable &&
                    renderNode.parent?.node?.type !== 'end_conversation' && (
                      <>
                        <ArcedPath
                          inPoint={inPoint}
                          outPoint={startPoint}
                          turnPoint={turnPoint}
                          isInvalid={edgeError}
                        />
                        {shouldShowAddButton && (
                          <AddNodeButton
                            style={{
                              left: inPoint.x - halfButtonSize,
                              top: inPoint.y - halfButtonSize - nodePad,
                            }}
                            dispatch={dispatch}
                            useCase={graph.use_case}
                            edgeId={parentEdge.id}
                            isSms={isSms}
                            graph={defaultGraph}
                          />
                        )}
                      </>
                    );
                } else {
                  pathContent = null;
                }

                const isJumpable =
                  jumpingFromEdge &&
                  renderNode.parentEdge?.editable &&
                  jumpingFromEdge.from_node_id !== renderNode.node?.id;

                const nodeStyle = {
                  left: renderNode.left,
                  top: renderNode.top,
                } as const;

                const key =
                  renderNode.jump || !renderNode.node
                    ? renderNode.parentEdge?.id
                    : `node: ${renderNode.node.id}`;

                return (
                  <React.Fragment key={key}>
                    {pathContent}
                    {renderNode.jump && renderNode.node ? (
                      <JumpNodeView
                        node={renderNode.node}
                        style={nodeStyle}
                        edgeError={edgeError}
                        onRemove={
                          parentEdge
                            ? () =>
                                dispatch({
                                  type: 'TRIM_EDGE',
                                  payload: {edgeId: parentEdge.id},
                                })
                            : null
                        }
                        readOnly={readOnly}
                      />
                    ) : (
                      <>
                        {renderNode.node &&
                          renderNode.node.type !== 'end_conversation' && (
                            <ConversationNodeView
                              entityType={entityType}
                              node={renderNode.node}
                              graph={graph}
                              flows={flows}
                              flowsLoaded={loaded}
                              isFirstMessage={firstMessages.has(
                                renderNode.node,
                              )}
                              disabled={disabled}
                              style={nodeStyle}
                              isJumpable={isJumpable}
                              onRender={handleElementRender}
                              onSize={handleSize}
                              edgeError={edgeError}
                              nodeError={nodeError}
                              nodeWarning={nodeWarning}
                              isJourneyPreview={isJourneyPreview}
                            />
                          )}

                        {
                          // first condition here is original terminal edges
                          // that don't originate from an end conversation node
                          ((renderNode.children.length === 0 &&
                            renderNode.parent?.node?.type !==
                              'end_conversation') ||
                            // or.... an end conversation node
                            renderNode.node?.type === 'end_conversation') && (
                            <TerminalNode
                              // @ts-expect-error - TS2322 - Type '{ firstCondition: boolean; dispatch: ConversationDispatch; renderNode: RenderNode; readOnly: boolean; graph: EditableGraph; setShowFeedbackEditor: Dispatch<...>; defaultGraph: Graph; setFlow: ((arg1: Graph) => void) | undefined; }' is not assignable to type 'IntrinsicAttributes & { dispatch: any; renderNode: any; readOnly: any; graph: any; setShowFeedbackEditor: any; defaultGraph: any; setFlow: any; }'.
                              firstCondition={
                                renderNode.children.length === 0 &&
                                renderNode.node?.type !== 'end_conversation'
                              }
                              dispatch={dispatch}
                              renderNode={renderNode}
                              readOnly={readOnly}
                              graph={graph}
                              setShowFeedbackEditor={setShowFeedbackEditor}
                              defaultGraph={defaultGraph}
                              setFlow={setFlow}
                            />
                          )
                        }

                        {renderNode.branches &&
                          (renderNode.node?.type === 'condition' ? (
                            <ThenElseView branches={renderNode.branches} />
                          ) : (
                            <BranchesView
                              dispatch={dispatch}
                              branches={renderNode.branches}
                              renderNode={renderNode}
                              disabled={disabled}
                              onRender={handleElementRender}
                              onSize={handleSize}
                              error={error}
                            />
                          ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          {state.jumpingFrom && (
            <Shroud
              // @ts-expect-error - TS2769 - No overload matches this call.
              ref={shroudRef}
              onClick={() => dispatch({type: 'CANCEL_JUMP'})}
            />
          )}
        </ScrollAndControls>

        {showingShroud && <Shroud />}

        {editingNode &&
          editableNodeTypes.includes(editingNode.node.type) &&
          graph.bot_type === 'state_machine' && (
            <NodeEditor
              graph={graph}
              entityType={entityType}
              actionName="edit"
              onClose={() => dispatch({type: 'CANCEL_EDIT_NODE'})}
              onSubmit={(node) =>
                dispatch({type: 'UPDATE_NODE', payload: node})
              }
              defaultNode={editingNode.node}
              type={editingNode.node.type}
              readOnly={Boolean(disabled)}
            />
          )}
        {(state.insertingNode || editingNode) &&
          graph.bot_type === 'task_oriented' && (
            <>
              <Shroud onClick={handleCancelNodeEditorV2} />
              <GenericSidepanel
                type="GENERIC_MODAL"
                className={classify(css.sidepanel, extraSidepanelClassName)}
                // @ts-expect-error - TS2322 - Type '{ children: Element; type: string; className: string; headerContainerClassName: string; width: string; direction: "right"; title: any; removeModal: () => void; }' is not assignable to type 'IntrinsicAttributes & Base & { type: any; render?: undefined; text?: ReactNode; allowClickAway?: boolean | undefined; children?: ReactNode; ... 9 more ...; modalVersion?: "genesis" | undefined; } & { ...; }'.
                headerContainerClassName={css.sidepanelHeaderContainer}
                width={nodeType === 'job_match' ? '50%' : '33%'}
                direction="right"
                title={nodeType === 'question' ? '' : nodeTypes[nodeType]}
                removeModal={handleCancelNodeEditorV2}
              >
                <NodeEditorV2
                  graph={defaultGraph}
                  onCancel={handleCancelNodeEditorV2}
                  readOnly={defaultGraph.status !== 'init' || Boolean(disabled)}
                />
              </GenericSidepanel>
            </>
          )}

        {state.insertingNode &&
          insertingAboveRenderNode &&
          graph.bot_type === 'state_machine' && (
            <NodeEditor
              graph={graph}
              entityType={entityType}
              actionName="add"
              onClose={() => dispatch({type: 'CANCEL_INSERT'})}
              onSubmit={(node) =>
                dispatch({type: 'COMMIT_NODE', payload: node})
              }
              type={state.insertingNode.nodeType}
              edgeId={state.insertingNode.edgeId}
              readOnly={Boolean(disabled)}
            />
          )}

        {editingEdge && (
          <ConditionSelector
            dispatch={dispatch}
            content={content}
            edge={editingEdge}
          />
        )}

        {showFeedbackEditor && (
          <FeedbackEditor
            onClose={() => setShowFeedbackEditor(false)}
            editableGraph={graph}
            graph={defaultGraph}
            readOnly={readOnly}
            setFlow={setFlow}
            entityType={entityType}
          />
        )}
      </div>
    </SmsContext.Provider>
  );
}

function TerminalNode({
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  dispatch,
  // @ts-expect-error - TS7031 - Binding element 'renderNode' implicitly has an 'any' type.
  renderNode,
  // @ts-expect-error - TS7031 - Binding element 'readOnly' implicitly has an 'any' type.
  readOnly,
  // @ts-expect-error - TS7031 - Binding element 'graph' implicitly has an 'any' type.
  graph,
  // @ts-expect-error - TS7031 - Binding element 'setShowFeedbackEditor' implicitly has an 'any' type.
  setShowFeedbackEditor,
  // @ts-expect-error - TS7031 - Binding element 'defaultGraph' implicitly has an 'any' type.
  defaultGraph,
  // @ts-expect-error - TS7031 - Binding element 'setFlow' implicitly has an 'any' type.
  setFlow,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const latEnabled = useLiveAgentTransfer();
  const handleToggleNode = () => {
    const edgeId = renderNode.parentEdge?.id;
    if (renderNode.node?.type === 'end_conversation') {
      const node: EndConversationNode = renderNode.node;
      const behavior = node.additional_configs_json.behavior;

      if (edgeId != null) {
        dispatch({
          type: 'UPDATE_NODE',
          payload: {
            ...node,
            additional_configs_json: {
              behavior:
                behavior === 'end_conversation'
                  ? 'return_to_caller'
                  : 'end_conversation',
            },
          },
        });
      }
      return;
    } else if (edgeId != null) {
      dispatch({
        type: 'INSERT_NODE',
        payload: {
          node: createEndConversationNode('return_to_caller'),
          edgeId,
        },
      });
    }
  };

  // disabled here because we are using a separate static label for translation and endLabel cannot be used due to the string check below (endLabel === 'End')

  let endLabel = 'End'; // default
  let endLabelTranslated = t('End', 'End');
  let node: EndConversationNode | null | undefined;
  if (renderNode.node?.type === 'end_conversation') {
    node = renderNode.node;
    endLabel = {end_conversation: 'End', return_to_caller: 'End and return'}[
      // @ts-expect-error - TS18049 - 'node' is possibly 'null' or 'undefined'.
      node.additional_configs_json.behavior ?? 'end_conversation'
    ];

    endLabelTranslated = {
      end_conversation: t('End', 'End'),
      return_to_caller: t('End_and_return', 'End and return'),
      // @ts-expect-error - TS18049 - 'node' is possibly 'null' or 'undefined'.
    }[node.additional_configs_json.behavior ?? 'end_conversation'];
  }

  return (
    <div>
      <div
        className={css.endTag}
        style={{
          left: renderNode.left + (nodeWidth - tagWidth) / 2,
          top: renderNode.top,
        }}
      >
        <div>{endLabelTranslated}</div>
        {latEnabled && !readOnly && (
          <button
            style={{cursor: 'pointer', marginTop: -6}}
            onClick={handleToggleNode}
          >
            <GenesisIcon name="sort-down" type="solid" />
          </button>
        )}
      </div>

      {((node != null && endLabel === 'End') || node == null) && (
        <FeedbackNode
          style={{
            left: renderNode.left,
            top: renderNode.top + 30,
          }}
          editable={!readOnly}
          graph={graph}
          setShowFeedbackEditor={setShowFeedbackEditor}
          defaultGraph={defaultGraph}
          feedbackEnabled={defaultGraph.flow_config.feedback_enabled}
          setFlow={setFlow}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}

function ScrollAndControls({
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  children,
  // @ts-expect-error - TS7031 - Binding element 'state' implicitly has an 'any' type.
  state,
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  dispatch,
  // @ts-expect-error - TS7031 - Binding element 'contentHeight' implicitly has an 'any' type.
  contentHeight,
  // @ts-expect-error - TS7031 - Binding element 'contentWidth' implicitly has an 'any' type.
  contentWidth,
  // @ts-expect-error - TS7031 - Binding element 'validateDrag' implicitly has an 'any' type.
  validateDrag,
  // @ts-expect-error - TS7031 - Binding element 'isScrollable' implicitly has an 'any' type.
  isScrollable,
  // @ts-expect-error - TS7031 - Binding element 'disabled' implicitly has an 'any' type.
  disabled,
  // @ts-expect-error - TS7031 - Binding element 'onImageExport' implicitly has an 'any' type.
  onImageExport,
}) {
  const maxScale = 1;
  const minScale = 0.1;

  const [scale, setScale] = React.useState(1);

  const handleScaleChange = (scale: number) => {
    setScale(clamp(scale, minScale, maxScale));
  };

  return (
    <>
      <ScrollContainer
        className={css.scrollContainer}
        contentWidth={contentWidth}
        contentHeight={contentHeight}
        validateDrag={validateDrag}
        scale={scale}
        setScale={handleScaleChange}
        isScrollable={isScrollable}
      >
        {children}
      </ScrollContainer>
      <Controls
        className={css.controls}
        dispatch={dispatch}
        historyIndex={state.historyIndex}
        historySize={state.history.length}
        scale={scale}
        setScale={handleScaleChange}
        maxScale={maxScale}
        minScale={minScale}
        disabled={disabled}
        onImageExport={onImageExport}
      />
    </>
  );
}

// @ts-expect-error - TS7031 - Binding element 'branches' implicitly has an 'any' type.
function ThenElseView({branches}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  let thenBranch, elseBranch;
  if (branches[0].edge.conditions?.[0] === true) {
    [thenBranch, elseBranch] = branches;
  } else {
    [elseBranch, thenBranch] = branches;
  }
  return (
    <>
      <div
        className={css.ifTag}
        style={{left: thenBranch.left, top: thenBranch.top}}
      >
        {t('Then', 'Then')}
      </div>
      <div
        className={css.ifTag}
        style={{left: elseBranch.left, top: elseBranch.top}}
      >
        {t('Else', 'Else')}
      </div>
    </>
  );
}

function BranchesView({
  dispatch,
  renderNode,
  branches,
  disabled,
  onSize,
  onRender,
  error,
}: {
  dispatch: ConversationDispatch;
  renderNode: RenderNode;
  branches: Array<RenderBranch>;
  disabled?: unknown;
  onSize: (arg1: Record<any, any>, arg2: number) => unknown;
  onRender: (arg1: Record<any, any>) => unknown;
  error: Validation | null | undefined;
}) {
  const allowMeetingBranching = Boolean(
    useReleaseFlag('chatbotMeetingBranching'),
  );
  const {node} = renderNode;
  invariant(
    node && isBranchableNodeType(node),
    'Cannot render branches without node.children.',
  );

  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  const answerIndices = React.useMemo(
    () =>
      // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'. | TS7006 - Parameter 'answer' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
      new Map((node.answers || []).map((answer, index) => [answer.id, index])),
  );

  const usedConditions: Set<AnswerId> = new Set(
    branches
      .map(
        ({edge}) =>
          edge.conditions?.filter(
            (condition: ConditionValue) => typeof condition !== 'boolean',
          ) || [],
      )
      .flat(),
  );

  if (node.type === 'scheduler' && !allowMeetingBranching) {
    return null;
  }

  return (
    <>
      {branches.map((branch) => {
        onRender(branch.edge);
        return (
          <BranchView
            key={branch.edge.id}
            dispatch={dispatch}
            style={{
              width: branchWidth,
              left: branch.left,
              top: branch.top,
            }}
            onSize={onSize}
            // @ts-expect-error - TS2322 - Type 'ConditionNode | DispositionNode | DynamicQuestionNode | EndConversationNode | FileUploadNode | ... 8 more ... | TextToApplyNode' is not assignable to type 'LATNode | QuestionNode | SchedulerNode | TextToApplyNode'.
            node={node}
            edge={branch.edge}
            usedConditions={usedConditions}
            // @ts-expect-error - TS2322 - Type 'Map<unknown, unknown>' is not assignable to type 'Map<AnswerId, number>'.
            answerIndices={answerIndices}
            disabled={disabled}
            edgeError={error?.edges?.[String(branch.edge.id)]}
          />
        );
      })}
      {!disabled && (
        <AddButton
          // @ts-expect-error - TS2322 - Type '{ style: { left: number; top: number; }; onClick: () => void; }' is not assignable to type 'IntrinsicAttributes & RefAttributes<unknown>'.
          style={getBranchAddPosition(renderNode)}
          onClick={() =>
            dispatch({
              type: 'ADD_BRANCH',
              payload: {
                nodeId: node.id,
              },
            })
          }
        />
      )}
    </>
  );
}

// NOTE (kyle): converts the flat graph (many-to-many) to a doubly linked tree
// with cached size and depth values. this allows us to visualize the graph as
// a tree.
function createRenderTree(
  graph: EditableGraph,
  currentEdge: Edge,
  renderNodes: Map<ConversationNode, RenderNode> = new Map(),
  depth: number = 0,
): RenderNode {
  const root =
    currentEdge.to_node_id != null
      ? graph.nodes.get(currentEdge.to_node_id)
      : null;

  const renderNode = createRenderNode(root, depth, graph);
  renderNode.parentEdge = currentEdge;

  if (!root) {
    return renderNode;
  }

  const prevRenderNode = renderNodes.get(root);

  // TODO (kyle): if we want to use the shortest path, we need to check depth and
  // possibly swap here.
  if (prevRenderNode) {
    renderNode.jump = true;
    renderNode.size = jumpWidth + nodePad * 2;
    // NOTE (kyle): jump nodes are unique so we do not add this to
    // the cache.
    return renderNode;
  }

  renderNodes.set(root, renderNode);

  const childEdges = getChildEdges(graph, root.id);
  renderNode.childEdges = childEdges;

  if (childEdges.length > 0) {
    const {children} = renderNode;

    for (const child of childEdges) {
      const childRenderNode = createRenderTree(
        graph,
        child,
        renderNodes,
        depth + 1,
      );

      childRenderNode.parent = renderNode;
      children.push(childRenderNode);
    }

    // NOTE (kyle): only children should always be as big or greater than their
    // parents. this means jump nodes (which are small) will be centered under
    // their larger parents.
    if (children.length === 1) {
      const onlyChild = children[0];
      onlyChild.size = renderNode.size = Math.max(
        renderNode.size,
        onlyChild.size,
      );

      // NOTE (kyle): the size of a branched node is the sum of its children.
    } else if (children.length > 1) {
      renderNode.size = 0;
      for (const childRenderNode of children) {
        renderNode.size += childRenderNode.size;
      }
    }
  }

  return renderNode;
}

// NOTE (kyle): flattens RenderTree into a list of renderable nodes with
// position and size values. with this we can redner a simple keyed list
// of absolutely positioned elements.
function createRenderNode(
  node: ConversationNode | null | undefined,
  depth: number,
  graph: EditableGraph,
): RenderNode {
  const zeroPoint = {x: 0, y: 0} as const;
  return {
    node,
    parent: null,
    parentEdge: null,
    children: [],
    childEdges: [],
    index: 0,
    size: outerWidth,
    depth,
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
    inPoint: zeroPoint,
    outPoint: zeroPoint,
    startPoint: zeroPoint,
    turnPoint: 0,
    ...zeroPoint,
  } as RenderNode;
}

const nodePad = 40;
const quarterPad = nodePad / 2;
const tagHeight = 30;
const arcRadius = 5;
const bottomPad = 100; //for feedback customization card

const nodeWidth = parseInt(commonCss.nodeWidth);
const nodeHeight = parseInt(commonCss.nodeHeight);
const tagWidth = parseInt(commonCss.tagWidth);
const jumpWidth = parseInt(commonCss.jumpNodeWidth);

const minEdgeHeight = nodePad * 2;
const branchEdgeHeight = nodePad * 3;

const branchWidth = 200;
const branchPad = parseInt(commonCss.branchPadding);
const conditionHeight = parseInt(commonCss.conditionHeight);
const buttonSize = parseInt(commonCss.addButtonSize);
const halfButtonSize = buttonSize / 2;

const outerWidth = nodeWidth + nodePad * 2;
const outerHeight = nodeHeight + nodePad * 2;

// traverse the tree and return a flat array with positioning
// information for each node.
function createRenderContent(
  graph: EditableGraph,
  node: RenderNode,
  heightCache: Map<Record<any, any>, number>,
  offset = 0,
  index = 0,
  parent = null,
  nodeX = 0,
  content = [],
): RenderNode[] {
  const left = nodeX - nodeWidth / 2;

  const edgeHeight =
    // @ts-expect-error - TS2339 - Property 'branches' does not exist on type 'never'.
    parent && parent.branches ? branchEdgeHeight : minEdgeHeight;

  // TODO (kyle): make this dependent on anscestor height and all siblings
  // @ts-expect-error - TS2339 - Property 'bottom' does not exist on type 'never'.
  const top = parent ? parent.bottom + edgeHeight : offset + minEdgeHeight;

  const centerX = left + nodeWidth / 2;

  const bottom = top + (node.node && heightCache.get(node.node)) || nodeHeight;

  const renderNode: RenderNode = Object.assign(node, {
    ...node,
    parent,
    index,
    x: nodeX,
    y: node.depth,
    left,
    top,
    bottom,
    right: left + nodeWidth,
    inPoint: {
      x: centerX,
      y: top,
    },
    outPoint: {
      x: centerX,
      y: bottom,
    },
  });

  // @ts-expect-error - TS2345 - Argument of type 'RenderNode' is not assignable to parameter of type 'never'.
  content.push(renderNode);

  if (!renderNode.jump) {
    const {node} = renderNode;

    let branches;
    if (node) {
      // this is a branched node
      // @ts-expect-error - TS2339 - Property 'answers' does not exist on type 'ConversationNode'. | TS2339 - Property 'question_is_multiselect' does not exist on type 'ConversationNode'.
      if (node.answers && !node.question_is_multiselect) {
        branches = renderNode.childEdges.map((edge, index) => {
          const left =
            centerX - (renderNode.children.length / 2 - index) * branchWidth;
          const top = renderNode.bottom;
          // TODO (kyle): only add 1 if you can add more options
          const itemCount = edge.conditions
            ? (edge.conditions.length || 1) + 1
            : 1;
          const height = heightCache.get(edge) ?? 0;
          return {
            edge,
            left,
            top,
            height,
            outPoint: {
              x: left + branchWidth / 2,
              y: top + height,
            },
          };
        });
      } else if (node.type === 'condition') {
        const ifElseWidth = 52;
        const ifElseHeight = 28;

        branches = [-1, 1].map((distance, index) => {
          const left = centerX + 124 * distance;
          const top = renderNode.bottom;
          const edge = renderNode.childEdges[index];

          return {
            edge,
            left: left - ifElseWidth / 2,
            top,
            height: ifElseHeight,
            outPoint: {
              x: left,
              y: top + ifElseHeight,
            },
          };
        });
      } else if (node.type === 'text_to_apply') {
        branches = renderNode.childEdges.map((edge, index) => {
          const left =
            centerX - (renderNode.children.length / 2 - index) * branchWidth;
          const top = renderNode.bottom;

          const height = heightCache.get(edge) ?? 0;
          return {
            edge,
            left,
            top,
            height,
            outPoint: {
              x: left + branchWidth / 2,
              y: top + height,
            },
          };
        });
      } else if (node.type === 'scheduler') {
        branches = renderNode.childEdges.map((edge, index) => {
          const left =
            centerX - (renderNode.children.length / 2 - index) * branchWidth;
          const top = renderNode.bottom;

          const height = heightCache.get(edge) ?? 0;
          return {
            edge,
            left,
            top,
            height,
            outPoint: {
              x: left + branchWidth / 2,
              y: top + height,
            },
          };
        });
      }
    }
    if (branches) {
      Object.assign(renderNode, {
        branches,
        bottom: Math.max(...branches.map((branch) => branch.outPoint.y)),
      });
    }

    let x = nodeX - renderNode.size / 2;

    for (const [index, child] of renderNode.children.entries()) {
      createRenderContent(
        graph,
        child,
        heightCache,
        offset,
        index,
        // @ts-expect-error - TS2345 - Argument of type 'RenderNode' is not assignable to parameter of type 'null | undefined'.
        renderNode,
        x + child.size / 2,
        content,
      );
      x += child.size;
    }
  }

  return content;
}

// finalize the positions of the lines drawn between nodes
function finalizePaths(renderNodes: RenderNode[]): RenderNode[] {
  renderNodes.forEach((renderNode) => {
    if (renderNode.parentEdge) {
      const {inPoint, parent} = renderNode;
      let turnPoint;
      let startPoint;
      if (parent) {
        // TODO (kyle): this is a silly inefficiency
        const parentBranch =
          parent &&
          parent.branches &&
          parent.branches.find(
            (branch) => branch.edge.id === renderNode.parentEdge?.id,
          );

        startPoint = parentBranch ? parentBranch.outPoint : parent.outPoint;

        turnPoint = parentBranch
          ? startPoint.y + (inPoint.y - startPoint.y) / 3
          : startPoint.y + (inPoint.y - startPoint.y) / 2;
      } else {
        startPoint = {x: 0, y: tagHeight};
        turnPoint = startPoint.y + (inPoint.y - startPoint.y) / 2;
      }
      renderNode.turnPoint = turnPoint;
      renderNode.startPoint = startPoint;
    }
  });

  // for branched nodes, we have to make sure that their lines do not overlap.
  // if a node's previous sibling has an overlapping edge line, we nudge its line
  // up or down.
  for (const renderNode of renderNodes) {
    const {children} = renderNode;

    if (children.length > 1) {
      const half = children.length / 2;
      let head, tail;
      if (Number.isInteger(half)) {
        head = children.slice(0, half);
        tail = children.slice(half);
      } else {
        head = children.slice(0, Math.floor(half));
        tail = children.slice(Math.ceil(half));
      }

      for (const [index, renderNode] of head.entries()) {
        const previousSibling = head[index - 1];
        if (
          previousSibling &&
          previousSibling.turnPoint >= renderNode.turnPoint
        ) {
          renderNode.turnPoint = previousSibling.turnPoint + 5;
        }
      }

      for (const [index, renderNode] of tail.entries()) {
        const previousSibling = tail[index - 1];
        if (
          previousSibling &&
          previousSibling.turnPoint <= renderNode.turnPoint
        ) {
          renderNode.turnPoint = previousSibling.turnPoint - 5;
        }
      }
    }
  }

  return renderNodes;
}

// @ts-expect-error - TS2339 - Property 'onClick' does not exist on type '{}'. | TS2339 - Property 'style' does not exist on type '{}'.
const AddButton = React.forwardRef(({onClick, style}, ref) => (
  <Button
    className={css.addButton}
    ref={ref}
    size="small"
    round
    style={style}
    onClick={onClick}
  >
    <ComposeIcon />
  </Button>
));

function AddNodeButton({
  dispatch,
  edgeId,
  style,
  useCase,
  isSms,
  graph,
}: {
  dispatch: ConversationDispatch;
  edgeId: Id;
  style?: Record<any, any>;
  useCase: UseCase;
  isSms: boolean;
  graph: Graph;
}) {
  const [contextMenuRef, handleOpen] = useContextMenu2({
    menuContent: (
      <AddNodeOptions
        dispatch={dispatch}
        edgeId={edgeId}
        useCase={useCase}
        isSms={isSms}
        graph={graph}
      />
    ),
  });

  // @ts-expect-error - TS2322 - Type '{ style: Record<any, any> | undefined; ref: ReactRef<HTMLElement>; onClick: () => void; }' is not assignable to type 'IntrinsicAttributes & RefAttributes<unknown>'.
  return <AddButton style={style} ref={contextMenuRef} onClick={handleOpen} />;
}

function AddNodeOptions({
  dispatch,
  edgeId,
  useCase,
  isSms,
  graph,
}: {
  dispatch: ConversationDispatch;
  edgeId: Id;
  useCase: UseCase;
  isSms: boolean;
  graph: Graph;
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const reduxDispatch = useDispatch();
  const router = useHistory();
  const isMissionBot = graph.bot_type === 'task_oriented';

  const isRoot = !graph.edges.find(({id}) => id === edgeId);
  const getNewNodeHandler = (nodeType: EditableNodeType) => {
    dispatch({
      type: 'NEW_NODE',
      payload: {nodeType, edgeId},
    });
  };

  const handleOpenNodeEditorV2 = (nodeType: EditableNodeType) => {
    dispatch({
      type: 'NEW_NODE',
      payload: {
        nodeType,
        edgeId,
        question_type: nodeType === 'question' ? 'preset' : undefined,
      },
    });

    // router.push(`/conversations/${graph.id}/editor`);
  };

  const handleOpenNodeEditor = isMissionBot
    ? handleOpenNodeEditorV2
    : getNewNodeHandler;

  const options = [
    {
      label: t('Add_question', 'Add question'),
      action: () => handleOpenNodeEditor('question'),
    },
    {
      label: t('Add_message', 'Add message'),
      action: () => handleOpenNodeEditor('message'),
    },
    isRoot
      ? undefined
      : {
          label: t('Jump_to', 'Jump to'),
          action: () => dispatch({type: 'START_JUMP', payload: {edgeId}}),
        },
    // NOTE (kyle): add this back when we add the submenu
    /*
    {
      label: 'End conversation',
      action: () => dispatch({type: 'TRIM_EDGE', payload: {edgeId}}),
    },
    */
    ENABLE_PRESCREENING
      ? {
          label: t('View_more', 'View more'),
          submenu: (
            <AddNodeMoreOptions
              dispatch={dispatch}
              botType={graph.bot_type}
              useCase={useCase}
              edgeId={edgeId}
              isSms={isSms}
              handleOpenNodeEditor={handleOpenNodeEditor}
            />
          ),
        }
      : {
          label: t('Add_summary', 'Add summary'),
          action: () => handleOpenNodeEditor('summary'),
        },
  ].filter(Boolean);

  // @ts-expect-error - TS2322 - Type '({ label: string; action: () => void; submenu?: undefined; } | { label: string; submenu: Element; action?: undefined; } | undefined)[]' is not assignable to type '({ label: string; action: () => unknown; } | { label: string; submenu: ReactNode; })[]'.
  return <SuperMenu options={options} />;
}

function AddNodeMoreOptions({
  // @ts-expect-error - TS7031 - Binding element 'dispatch' implicitly has an 'any' type.
  dispatch,
  // @ts-expect-error - TS7031 - Binding element 'edgeId' implicitly has an 'any' type.
  edgeId,
  // @ts-expect-error - TS7031 - Binding element 'handleOpenNodeEditor' implicitly has an 'any' type.
  handleOpenNodeEditor,
  // @ts-expect-error - TS7031 - Binding element 'useCase' implicitly has an 'any' type.
  useCase,
  // @ts-expect-error - TS7031 - Binding element 'botType' implicitly has an 'any' type.
  botType,
  // @ts-expect-error - TS7031 - Binding element 'isSms' implicitly has an 'any' type.
  isSms,
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const schedulerEnabled = useSchedulerEnabled();
  const schedulerIntegrationEnabled = useReleaseFlag(
    'chatbotSchedulerIntegration',
  );
  const outboundJobMatchEnabled = useAgencyConfig(
    'enableJobMatchForOnboundChatbot',
  );
  const inboundJobMatchEnabled = useAgencyConfig(
    'enableJobMatchForInboundChatbot',
  );
  const showJobMatch = outboundJobMatchEnabled || inboundJobMatchEnabled;
  const showFileUpload = useReleaseFlag('chatbotFileUpload');

  const latIntegrationEnabled =
    useLiveAgentTransfer() && botType === 'task_oriented';

  const seesDynamicQuestions =
    useApplicationChatbot() &&
    botType === 'task_oriented' &&
    useCase === 'application_inbound_web';

  const textToApplyNodeEnabled =
    useReleaseFlag('chatbotTextToApplyNode') && useCase === 'sourcing';

  const options = [
    {
      label: t('Add_notification', 'Add notification'),
      action: () => {
        handleOpenNodeEditor('notification');
      },
    },
    {
      label: t('Add_condition', 'Add condition'),
      action: () => handleOpenNodeEditor('condition'),
    },
    {
      label: t('Add_disposition', 'Add disposition'),
      action: () => handleOpenNodeEditor('disposition'),
    },
    {
      label: t('Add_summary', 'Add summary'),
      action: () => handleOpenNodeEditor('summary'),
    },
    showJobMatch
      ? {
          label: t('Add_job_match', 'Add job match'),
          action: () => handleOpenNodeEditor('job_match'),
        }
      : null,
    showFileUpload
      ? {
          label: t('Add_file_upload', 'Add file upload'),
          action: () => handleOpenNodeEditor('file_upload'),
        }
      : null,
    schedulerIntegrationEnabled && schedulerEnabled
      ? {
          label: t('Add_meeting', 'Add meeting'),
          action: () => handleOpenNodeEditor('scheduler'),
        }
      : null,
    latIntegrationEnabled
      ? {
          label: t('Add_Live_Chat', 'Add Live Chat'),
          action: () => handleOpenNodeEditor('live_agent_transfer'),
        }
      : null,
    latIntegrationEnabled
      ? {
          label: t('Add_Link_to_Conversation', 'Add Link to Conversation'), // TK is this really called that???
          action: () => handleOpenNodeEditor('link_to_flow'),
        }
      : null,
    seesDynamicQuestions
      ? {
          label: t('Add_Dynamic_Question', 'Add Dynamic Question'),
          action: () => handleOpenNodeEditor('dynamic_question'),
        }
      : null,
    textToApplyNodeEnabled
      ? {
          label: t('Add_Text_to_Apply', 'Add Text to Apply'),
          action: () => handleOpenNodeEditor('text_to_apply'),
        }
      : null,
  ];

  const filteredList: Array<
    | {
        label: string;
        action: () => unknown;
      }
    | {
        label: string;
        submenu: React.ReactNode;
      }
  > = options.filter((x) => x != null);

  return <SuperMenu options={filteredList} />;
}

function getBranchAddPosition(renderNode: RenderNode) {
  const {branches} = renderNode;
  invariant(branches, 'Cannot get branch position without branches.');

  const rightMostBranch = last(branches);

  return {
    // @ts-expect-error - TS18048 - 'rightMostBranch' is possibly 'undefined'.
    left: rightMostBranch.left + branchWidth + 10,
    // @ts-expect-error - TS18048 - 'rightMostBranch' is possibly 'undefined'. | TS18048 - 'rightMostBranch' is possibly 'undefined'.
    top: rightMostBranch.top + rightMostBranch.height / 2 - halfButtonSize,
  };
}

function JumpNodeView({
  onRemove,
  node,
  style,
  edgeError,
  readOnly,
}: {
  onRemove: (() => unknown) | null | undefined;
  node: ConversationNode;
  style: Record<any, any>;
  edgeError: Validation | null | undefined;
  readOnly: boolean;
}) {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const errors = edgeError?.errors;
  const errorMessage = errors
    ? errors.includes('REDUNDANT_EDGE')
      ? `${t(
          'This_path_is_redundant_because_there_is_already_a_response_path_that_leads_to_this_node',
          'This path is redundant because there is already a response path that leads to this node',
        )}.`
      : errors.includes('CIRCULAR_EDGE')
      ? `${t(
          'This_path_loops_back_to_a_previous_node',
          'This path loops back to a previous node',
        )}.`
      : null
    : null;

  return (
    <div className={css.jumpNode} style={style}>
      {readOnly ? (
        <UnstyledButton className={css.jumpNodeLabel}>
          {t('Jump_to', 'Jump to')}
        </UnstyledButton>
      ) : (
        <UnstyledButton
          className={classify(
            css.jumpNodeLabel,
            errorMessage && css.jumpNodeError,
          )}
          onClick={onRemove}
        >
          <span>{t('Jump_to', 'Jump to')}</span>
          <span>{t('Remove', 'Remove')}</span>
        </UnstyledButton>
      )}
      <div
        className={classify(
          css.jumpNodeName,
          errorMessage && css.jumpNodeError,
        )}
      >
        “{node.name}”
        {errorMessage && (
          <HoverError className={css.jumpError} error={errorMessage} />
        )}
      </div>
    </div>
  );
}

function FocusHandler({onBlur}: {onBlur: () => unknown}) {
  // @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
  const ref = React.useRef();
  React.useEffect(() => {
    // @ts-expect-error - TS2339 - Property 'focus' does not exist on type '{}'.
    ref.current?.focus();
  }, []);

  return (
    <div
      // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'number'.
      tabIndex="0"
      // @ts-expect-error - TS2322 - Type 'RefObject<unknown>' is not assignable to type 'Ref<HTMLDivElement> | undefined'.
      ref={ref}
      onBlur={onBlur}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.currentTarget.blur();
        }
      }}
    />
  );
}

function useModifierKeys(keys: string[]): Set<string> {
  const [currentKeys, setKeys] = React.useState(new Set());

  React.useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (keys.includes(event.key)) {
        setKeys(new Set(currentKeys).add(event.key));
      }
    };

    const handleKeyUp = (event: any) => {
      if (keys.includes(event.key)) {
        const nextSet = new Set(currentKeys);
        nextSet.delete(event.key);
        setKeys(nextSet);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // @ts-expect-error - TS2322 - Type 'Set<unknown>' is not assignable to type 'Set<string>'.
  return currentKeys;
}
